import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, deleteToken, isSupported, onMessage } from 'firebase/messaging';
import api from './api';
import { getOrCreatePushDeviceId } from '../utils/pushDeviceId';

const PUSH_REGISTERED_TOKEN_KEY = 'js_push_registered_token';
const PUSH_SYNC_STATE_KEY = 'js_push_sync_state';

let firebaseApp = null;
let messagingInstance = null;
let cachedConfig = null;

async function fetchPushConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }
  const response = await api.get('/push/config');
  if (!response?.success || !response?.data) {
    throw new Error(response?.message || 'Push configuration unavailable');
  }
  cachedConfig = response.data;
  return cachedConfig;
}

function getFirebaseApp(config) {
  if (firebaseApp) {
    return firebaseApp;
  }
  const existing = getApps();
  if (existing.length) {
    firebaseApp = existing[0];
    return firebaseApp;
  }
  firebaseApp = initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    storageBucket: config.storageBucket,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId
  });
  return firebaseApp;
}

async function getMessagingInstance(config) {
  const supported = await isSupported();
  if (!supported) {
    throw new Error('Push notifications are not supported in this browser');
  }
  if (messagingInstance) {
    return messagingInstance;
  }
  const app = getFirebaseApp(config);
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

function toSwFirebaseConfig(config) {
  const { vapidKey, pushBackendConfigured, ...firebaseConfig } = config;
  return firebaseConfig;
}

async function postConfigToServiceWorker(registration, config) {
  const swConfig = toSwFirebaseConfig(config);
  const worker = registration.installing || registration.waiting || registration.active;
  if (worker) {
    worker.postMessage({ type: 'INIT_FIREBASE', config: swConfig });
  }
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }
  const config = await fetchPushConfig();
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/'
  });
  await navigator.serviceWorker.ready;
  await postConfigToServiceWorker(registration, config);
  const swConfig = toSwFirebaseConfig(config);
  const active = registration.active || registration.waiting || registration.installing;
  if (active) {
    active.postMessage({ type: 'INIT_FIREBASE', config: swConfig });
  }
  // Edge sometimes needs a beat before getToken() after SW init
  await new Promise((resolve) => setTimeout(resolve, 300));
  return registration;
}

async function ensureNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications are not supported in this browser');
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    throw new Error(
      'Notifications are blocked for this site. Open the lock icon next to the address bar → Site settings → allow Notifications, then try again.'
    );
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  return permission;
}

export async function subscribeToPushNotifications() {
  const config = await fetchPushConfig();
  if (!config.vapidKey) {
    throw new Error('Web push VAPID key is not configured on the server');
  }

  await ensureNotificationPermission();

  const registration = await registerServiceWorker();
  const messaging = await getMessagingInstance(config);
  const fcmToken = await getToken(messaging, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration
  });

  if (!fcmToken) {
    throw new Error('Failed to obtain FCM token');
  }

  const deviceId = getOrCreatePushDeviceId();
  const lastRegisteredToken = sessionStorage.getItem(PUSH_REGISTERED_TOKEN_KEY);
  if (lastRegisteredToken === fcmToken) {
    sessionStorage.setItem(PUSH_SYNC_STATE_KEY, 'subscribed');
    return { fcmToken, deviceId };
  }

  await api.post('/push/register', {
    fcm_token: fcmToken,
    platform: 'web',
    device_id: deviceId
  });
  sessionStorage.setItem(PUSH_REGISTERED_TOKEN_KEY, fcmToken);
  sessionStorage.setItem(PUSH_SYNC_STATE_KEY, 'subscribed');

  return { fcmToken, deviceId };
}

export async function unsubscribeFromPushNotifications() {
  const deviceId = getOrCreatePushDeviceId();

  if (sessionStorage.getItem(PUSH_SYNC_STATE_KEY) === 'unsubscribed') {
    return;
  }

  try {
    const config = await fetchPushConfig();
    const messaging = await getMessagingInstance(config);
    await deleteToken(messaging);
  } catch {
    // Token may already be invalid
  }

  try {
    await api.post('/push/unregister', {
      device_id: deviceId,
      platform: 'web'
    });
  } catch {
    // User may already be logged out during sync
  }

  sessionStorage.removeItem(PUSH_REGISTERED_TOKEN_KEY);
  sessionStorage.setItem(PUSH_SYNC_STATE_KEY, 'unsubscribed');
}

/**
 * Align server push registration with the browser Notification permission.
 * granted → subscribe; denied → unsubscribe; default → no-op.
 */
export async function syncPushWithBrowserPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { status: 'unsupported' };
  }

  const permission = Notification.permission;

  if (permission === 'granted') {
    await subscribeToPushNotifications();
    return { status: 'subscribed', permission };
  }

  if (permission === 'denied') {
    await unsubscribeFromPushNotifications();
    return { status: 'unsubscribed', permission };
  }

  return { status: 'default', permission };
}

export async function isPushSupported() {
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export function clearPushRegistrationCache() {
  sessionStorage.removeItem(PUSH_REGISTERED_TOKEN_KEY);
  sessionStorage.removeItem(PUSH_SYNC_STATE_KEY);
}

export function clearPushConfigCache() {
  cachedConfig = null;
}

/** Whether API/user flag allows push (handles true/1 and false/0). */
export function isPushPreferenceEnabled(value) {
  if (value === false || value === 0 || value === '0') {
    return false;
  }
  return true;
}

/**
 * Show a native OS/browser notification (not an in-app toast or admin panel).
 */
export function showBrowserNotification(payload) {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }
  if (Notification.permission !== 'granted') {
    return null;
  }

  const title =
    payload?.notification?.title || payload?.data?.title || 'JetScanner360';
  const body = payload?.notification?.body || payload?.data?.body || '';
  let url =
    payload?.data?.url ||
    payload?.fcmOptions?.link ||
    payload?.notification?.click_action ||
    '/';

  if (url && !url.startsWith('http')) {
    const origin = window.location.origin;
    url = `${origin}${url.startsWith('/') ? url : `/${url}`}`;
  }

  try {
    const tag =
      payload?.data?.notification_id ||
      payload?.data?.type ||
      payload?.data?.event_id ||
      'jetscanner-push';

    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag,
      renotify: false,
      data: { url }
    });

    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      if (url && url !== '/') {
        window.location.href = url;
      }
      notification.close();
    };

    return notification;
  } catch (err) {
    console.warn('Browser notification failed:', err);
    return null;
  }
}

export async function setupForegroundMessageListener(onPayload) {
  const config = await fetchPushConfig();
  const messaging = await getMessagingInstance(config);
  onMessage(messaging, (payload) => {
    showBrowserNotification(payload);
    if (typeof onPayload === 'function') {
      onPayload(payload);
    }
  });
}

/** Call before logout while auth token is still valid */
export async function sendTestPush() {
  const response = await api.post('/push/test');
  if (!response?.success) {
    throw new Error(response?.message || 'Test push failed');
  }
  return response;
}

export async function getPushConfigStatus() {
  try {
    const response = await api.get('/push/config');
    return { ok: true, data: response.data };
  } catch (err) {
    return {
      ok: false,
      message: err?.message || err?.data?.message || 'Push config unavailable',
      missing: err?.data?.missing || err?.data?.data?.missing
    };
  }
}

/**
 * FCM fields to include on POST /auth/login or /2fa/verify-* when permission is already granted.
 */
export async function getWebPushCredentialsForAuth() {
  if (typeof window === 'undefined' || Notification.permission !== 'granted') {
    return null;
  }
  try {
    const config = await fetchPushConfig();
    if (!config.vapidKey) {
      return null;
    }
    const registration = await registerServiceWorker();
    const messaging = await getMessagingInstance(config);
    const fcmToken = await getToken(messaging, {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration: registration
    });
    if (!fcmToken) {
      return null;
    }
    return {
      fcm_token: fcmToken,
      platform: 'web',
      device_id: getOrCreatePushDeviceId()
    };
  } catch {
    return null;
  }
}

/** Body for POST /auth/logout (replaces separate /push/unregister). */
export function buildLogoutPushBody() {
  return {
    device_id: getOrCreatePushDeviceId(),
    platform: 'web'
  };
}
